import React, { useContext, useEffect, useState } from "react";
import {
  IonHeader,
  IonToolbar,
  IonPage,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonFooter,
  IonText,
  useIonAlert,
} from "@ionic/react";
import {
  briefcaseOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  closeOutline,
  megaphoneOutline,
  peopleOutline,
  shieldCheckmark,
  shieldHalf,
  sparkles,
  sparklesOutline,
  starHalfOutline,
  starOutline,
  trophy,
  trophyOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";
import "../theme/AppStyles.css";
import { Capacitor } from "@capacitor/core";
import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor";
import { ProfileContext } from "../context/ProfileContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { LoadingScreen } from "../components/LoadingScreen";
import { fx_FB_UpdateUserLastRole } from "../functions/Fx_FirebaseUpdateFunctions";

const Paywall: React.FC = () => {
  const history = useHistory<any>();
  const { profile, subscriptionDispatch } = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState(false);
  const [present] = useIonAlert();

  const handleReturnToLogin = async () => {
    try {
      await signOut(auth)
        .then(() => {
          // console.log(profile.UserId);
        })
        .then(() => {
          history.push("./login");
        });
    } catch (err) {
      // console.log(err);
    }
  };

  const handleClosePresent = () => {
    setIsLoading(false);
  };

  // After a successful Scheduler subscription (new purchase OR restore),
  // flip the user's role flag in Firestore so Home renders the Scheduler
  // view and the SchedulerOnboarding modal triggers. Without this, a brand
  // new user coming from Werker Onboarding -> "Start Free Trial" -> Paywall
  // lands on Home as a Werker because `userLastRoleScheduler` is still false.
  const promoteToSchedulerIfNeeded = async () => {
    if (!profile?.UserId) return;
    if (profile.UserLastRoleScheduler === true) return;
    try {
      await fx_FB_UpdateUserLastRole(profile.UserId, true);
    } catch (err) {
      console.error("Failed to promote user to Scheduler role:", err);
    }
  };

  const handleRestorePurchases = async () => {
    setIsLoading(true);
    // if on an Apple device, check for subscriptions in Android
    // if on an Android device, check for subscriptions in Apple

    if (Capacitor.getPlatform() === "ios") {
      await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
      const _configRevCatUser = await Purchases.configure({
        apiKey: "appl_iMWtgbqUuMgwKiPchGQmfthxxRc",
        appUserID: profile.UserId, // Optional
      });
    }
    if (Capacitor.getPlatform() === "android") {
      await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
      await Purchases.configure({
        apiKey: "goog_qnZCUjgYjODmYqsdQefMSwiVFRc",
        appUserID: profile.UserId, // Optional
      });
    }

    const entitlementsList = await Purchases.getCustomerInfo();
    // console.log("entitlements, ", entitlementsList.customerInfo.entitlements.active);

    if (entitlementsList.customerInfo.entitlements.active["Werker Subscription"]) {
      // console.log("found a Werker Subscription");
      subscriptionDispatch({
        type: "REFRESH_SUBSCRIPTION",
        payload: 1,
      });
      history.push(`/Splash`);
    } else if (entitlementsList.customerInfo.entitlements.active["Scheduler Subscription"]) {
      // console.log("found a Scheduler Subscription");
      subscriptionDispatch({
        type: "REFRESH_SUBSCRIPTION",
        payload: 1,
      });
      await promoteToSchedulerIfNeeded();
      history.push(`/Splash`);
    } else {
      // console.log("didn't find a subscription");

      present({
        header: "No subscription found",
        buttons: [{ text: "Ok", handler: handleClosePresent }],
      });
    }
  };

  const handleSubscriptionPurchase = (subscriptionType: string) => {
    setIsLoading(true);

    const RevenueCatFX = async () => {
      // Set RevenueCat Config
      if (Capacitor.getPlatform() === "ios") {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // Enable to get debug logs
        await Purchases.configure({
          apiKey: "appl_iMWtgbqUuMgwKiPchGQmfthxxRc",
          appUserID: profile.UserId, // Optional
        });
      } else if (Capacitor.getPlatform() === "android") {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // Enable to get debug logs
        await Purchases.configure({
          apiKey: "goog_qnZCUjgYjODmYqsdQefMSwiVFRc",
          appUserID: profile.UserId, // Optional
        });
      }

      // Process Transaction
      if (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android") {
        const offeringsList = await Purchases.getOfferings();
        // console.log(offeringsList);

        // FOR FUTURE REFERENCE - USED TO PULL / ANALYZE REVENUECAT PACKAGES
        // const availablePackages = offeringsList.current?.availablePackages;
        // availablePackages?.forEach((identifiedPackage) => {
        //   console.log("identifiedPackage", identifiedPackage);
        //   Package 0 = Scheduler
        //   Package 1 = Werker
        // });
        var packageIndex: number = 10;
        if (subscriptionType === "monthly_Scheduler") {
          packageIndex = 0;
        } else if (subscriptionType === "monthly_Werker") {
          packageIndex = 1;
        }

        if (packageIndex < 10) {
          try {
            const availablePackage = offeringsList.current?.availablePackages[packageIndex];
            if (availablePackage) {
              await Purchases.purchasePackage({
                aPackage: availablePackage,
              })
                .then(() => {
                  subscriptionDispatch({
                    type: "REFRESH_SUBSCRIPTION",
                    payload: 1,
                  });
                })
                .then(async (_pResult) => {
                  // console.log("pResult, ", pResult);
                  // console.log("purchaseResult", purchaseResult);
                  if (subscriptionType === "monthly_Scheduler") {
                    await promoteToSchedulerIfNeeded();
                  }
                  setIsLoading(false);
                  navigateAfterPurchase();
                });
            }
          } catch (e) {
            console.error("error, ", e);
            subscriptionDispatch({
              type: "REFRESH_SUBSCRIPTION",
              payload: 1,
            });
            setIsLoading(false);
            navigateAfterPurchase();
          }
        }
      } else {
        // if (Capacitor.getPlatform() === 'web')
        setIsLoading(false);
      }
    };
    RevenueCatFX();
  };

  // useEffect(() => {
  //   console.log(history.location.state.cameFrom);
  //   console.log(profile.SubscriptionType);
  // }, []);

  const cameFrom = history.location.state?.cameFrom ?? "Home.Scheduler";
  const postCreate = history.location.state?.postCreate as
    | {
        jobId?: string;
        seriesId?: string;
        jobIds?: string[];
        hasAssociates?: boolean;
        cameFromDetail?: boolean;
      }
    | undefined;

  // Safety net: subscribed Schedulers should never see the upsell Paywall.
  // If they land here (direct URL, stale navigation, or an unguarded caller),
  // bail out immediately to the appropriate post-paywall destination.
  useEffect(() => {
    if (profile.SubscriptionType !== "Scheduler") return;
    if (cameFrom === "ScheduleJob" && postCreate) {
      if (postCreate.seriesId) {
        history.replace({
          pathname: `/SelectWerkersSeries/${postCreate.seriesId}`,
          state: {
            seriesId: postCreate.seriesId,
            jobIds: postCreate.jobIds,
            from: "ScheduleJob",
            cameFromDetail: !!postCreate.cameFromDetail,
          },
        });
        return;
      }
      if (postCreate.hasAssociates && postCreate.jobId) {
        history.replace({
          pathname: `/SelectWerkers/${postCreate.jobId}`,
          state: { from: "ScheduleJob" },
        });
        return;
      }
      if (postCreate.jobId) {
        history.replace(`/SchedShiftDetail/${postCreate.jobId}`);
        return;
      }
    }
    if (cameFrom === "Home.CreateJob") {
      history.replace({ pathname: "/ScheduleJob", state: { from: "Home" } });
      return;
    }
    history.replace(`/Splash`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.SubscriptionType]);

  const handleOnClose = () => {
    // console.log("close upgrade screen");
    if (cameFrom === "ScheduleJob" && postCreate?.jobId) {
      history.replace(`/SchedShiftDetail/${postCreate.jobId}`);
      return;
    }
    if (cameFrom === "Home.CreateJob") {
      history.replace({ pathname: "/ScheduleJob", state: { from: "Home" } });
      return;
    }
    history.push("/splash");
  };

  const navigateAfterPurchase = () => {
    if (cameFrom === "Home.CreateJob") {
      history.replace({ pathname: "/ScheduleJob", state: { from: "Home" } });
      return;
    }
    if (cameFrom === "ScheduleJob" && postCreate) {
      if (postCreate.seriesId) {
        history.replace({
          pathname: `/SelectWerkersSeries/${postCreate.seriesId}`,
          state: {
            seriesId: postCreate.seriesId,
            jobIds: postCreate.jobIds,
            from: "ScheduleJob",
            cameFromDetail: !!postCreate.cameFromDetail,
          },
        });
        return;
      }
      if (postCreate.hasAssociates && postCreate.jobId) {
        history.replace({
          pathname: `/SelectWerkers/${postCreate.jobId}`,
          state: { from: "ScheduleJob" },
        });
        return;
      }
      if (postCreate.jobId) {
        history.replace(`/SchedShiftDetail/${postCreate.jobId}`);
        return;
      }
    }
    history.push(`/Splash`);
  };

  const schedulerFeatures = [
    { icon: briefcaseOutline, text: "Create and manage jobs" },
    { icon: peopleOutline, text: "Invite and schedule Werkers" },
    { icon: peopleOutline, text: "Create and manage crews" },
    { icon: megaphoneOutline, text: "Send announcements" },
  ];

  return (
    <IonPage className={isLoading === true ? "bckgrnd" : ""}>
      {isLoading === true && <LoadingScreen LoadingText={"Processing"} />}

      {((isLoading === false &&
        cameFrom === "Profile" &&
        (profile.SubscriptionType === "" || profile.SubscriptionType === "NONE")) ||
        (isLoading === false &&
          (cameFrom === "SelectWerkers" ||
            cameFrom === "SelectWerkersSeries" ||
            cameFrom === "Home.Scheduler" ||
            cameFrom === "Home.CreateJob" ||
            cameFrom === "ScheduleJob" ||
            (cameFrom === "Profile" && profile.SubscriptionType === "Werker")))) && (
        <>
          <IonContent
            // fullscreen
            style={{
              "--background": "#0b0b0b",
              color: "#ffffff",
            }}
          >
            <div
              style={{
                paddingLeft: "1.25rem",
                paddingRight: "1.25rem",
                paddingBottom: "1.25rem",
                paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)",
                maxWidth: "520px",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "2rem",
                }}
              >
                <div style={{ fontSize: "1.55rem", fontWeight: 900 }}>WerkApp - Scheduler</div>

                <IonIcon
                  icon={closeOutline}
                  size="large"
                  onClick={handleOnClose}
                  style={{ cursor: "pointer", color: "#ffffff" }}
                />
              </div>

              <IonCard
                style={{
                  marginTop: "2rem",
                  borderRadius: "1.35rem",
                  background: "#161616",
                  border: "2px solid #ffc409",
                  color: "#ffffff",
                  boxShadow: "0 0 24px rgba(255, 212, 0, 0.22)",
                }}
              >
                <IonCardContent style={{ padding: "1.25rem" }}>
                  <div
                    style={{
                      display: "inline-block",
                      background: "#ffc409",
                      color: "#000",
                      fontWeight: 900,
                      fontSize: "0.75rem",
                      padding: "0.35rem 0.7rem",
                      borderRadius: "0.45rem",
                      marginBottom: "0.9rem",
                    }}
                  >
                    SCHEDULER
                  </div>

                  <h2
                    style={{
                      fontSize: "1.85rem",
                      margin: 0,
                      fontWeight: 900,
                    }}
                  >
                    Start Scheduling
                  </h2>

                  <p
                    style={{
                      color: "#d7d7d7",
                      fontSize: "0.95rem",
                      lineHeight: "1.45",
                      marginTop: "0.65rem",
                    }}
                  >
                    Unlock tools to create jobs, invite Werkers, fill shifts, create crews, and keep organized.
                  </p>

                  <div style={{ marginTop: "1.25rem" }}>
                    {[
                      "Create and manage jobs",
                      "Invite and schedule Werkers",
                      "Create and manage crews",
                      "Send announcements",
                      // "Keep shifts organized",
                    ].map((item) => (
                      <div
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.55rem",
                          marginTop: "0.75rem",
                        }}
                      >
                        <IonIcon icon={checkmarkCircle} style={{ color: "#ffc409", fontSize: "1.2rem" }} />
                        <span style={{ fontSize: "0.95rem", fontWeight: 600 }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </IonCardContent>
              </IonCard>

              <IonCard
                style={{
                  marginTop: "1rem",
                  borderRadius: "1.35rem",
                  background: "#ffffff",
                  color: "#000000",
                  boxShadow: "none",
                }}
              >
                <IonCardContent style={{ padding: "1.25rem", textAlign: "center" }}>
                  {/* <div style={{ fontSize: "1.35rem", fontWeight: 900 }}>10-Day Free Trial</div> */}
                  <div
                    style={{
                      fontSize: "1.35rem",
                      fontWeight: 900,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.35rem",
                    }}
                  >
                    <span>10-Day Free Trial</span>
                    <IonIcon
                      icon={shieldCheckmark}
                      style={{
                        color: "#ffc409",
                        fontSize: "1.2rem",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      color: "#555555",
                      fontSize: "0.95rem",
                      marginTop: "0.35rem",
                    }}
                  >
                    Then <strong>$29.99/month.</strong> Cancel anytime.
                  </div>

                  <IonButton
                    expand="block"
                    onClick={() => handleSubscriptionPurchase("monthly_Scheduler")}
                    style={{
                      "--background": "#ffc409",
                      "--color": "#000000",
                      "--border-radius": "0.9rem",
                      height: "3.25rem",
                      fontSize: "1.05rem",
                      fontWeight: 900,
                      marginTop: "1.1rem",
                    }}
                  >
                    Start My Free Trial
                  </IonButton>

                  {/* <IonText
              style={{
                color: "#666666",
                fontSize: "0.8rem",
                marginTop: "0.65rem",
                display: "block",
              }}
            >
              No charge for 10 days.
            </IonText> */}
                </IonCardContent>
              </IonCard>

              <IonButton
                expand="block"
                fill="clear"
                onClick={handleOnClose}
                style={{
                  "--color": "#d7d7d7",
                  marginTop: "0.75rem",
                  fontWeight: 700,
                }}
              >
                Continue as Werker — Free
              </IonButton>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "0.75rem",
                  marginTop: "1rem",
                  paddingBottom: "1.5rem",
                }}
              >
                <IonButton fill="clear" size="small" onClick={handleReturnToLogin} style={{ "--color": "#8f8f8f" }}>
                  Return to Login
                </IonButton>

                <IonButton fill="clear" size="small" onClick={handleRestorePurchases} style={{ "--color": "#8f8f8f" }}>
                  Restore Purchases
                </IonButton>
              </div>
              <div style={{ marginTop: "2rem", textAlign: "center" }}>
                <img
                  src="../assets/gorillaJones.jpeg" // replace with your actual path if different
                  alt="Gorilla Jones Easter Egg"
                  style={{ width: "150px", opacity: 0.6, marginTop: "2rem" }}
                />{" "}
                <IonText color="medium" style={{ fontSize: "0.75rem", display: "block", marginTop: "0.5rem" }}>
                  {" "}
                  Gorilla Jones approves this upgrade 💪{" "}
                </IonText>{" "}
              </div>
            </div>
          </IonContent>
        </>
      )}
      {/* Werker subscription section removed — Werker mode is free */}
    </IonPage>
  );
};

export default Paywall;
